/**
 * NCC: the Cheese Box Club purchase options (blocks/ncc-selling-plan-picker.liquid).
 *
 * The block renders one panel per plan group - Subscribe (Seal's rolling rule) and Gift (its prepaid rule) - each
 * with a native select that is form-associated with the section's product form. This element switches the
 * panels: the inactive panel is hidden and its fields disabled, so only the active select posts `selling_plan`
 * and the gift message only travels with a gift plan. On every change it copies the chosen plan's price and
 * description under the select (the price note is a polite live region, written only when it changes), writes
 * the plan's price into the page's price block and the sticky bar, which Horizon only refreshes on a variant
 * change, and swaps the Add to Bag button's label to the gift wording while a gift plan is chosen. The gift
 * message textarea carries its `name` only while it holds text, so an empty message never reaches the order.
 * A variant change re-renders the price block from the server's HTML; the fresh markup also carries this block,
 * so the options' prices are refreshed from it and the plan price written again.
 */

import { StandardEvents } from '@shopify/events';

class PlanPicker extends HTMLElement {
  /** @type {string | null} */
  #ctaSubscribe = null;

  connectedCallback() {
    this.addEventListener('click', this.#onClick);
    this.addEventListener('keydown', this.#onKeydown);
    this.addEventListener('change', this.#onChange);
    this.addEventListener('input', this.#onInput);
    this.#section?.addEventListener(StandardEvents.productSelect, this.#onProductSelect);
    this.#apply();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
    this.removeEventListener('keydown', this.#onKeydown);
    this.removeEventListener('change', this.#onChange);
    this.removeEventListener('input', this.#onInput);
    this.#section?.removeEventListener(StandardEvents.productSelect, this.#onProductSelect);
  }

  /** @returns {Element | null} */
  get #section() {
    return this.closest('.shopify-section');
  }

  /** @returns {HTMLElement[]} */
  get #tabs() {
    return [...this.querySelectorAll('[role="tab"]')];
  }

  /** @returns {HTMLElement[]} */
  get #panels() {
    return [...this.querySelectorAll('.ncc-plan-picker__panel')];
  }

  /** @returns {HTMLSelectElement | null} */
  get #activeSelect() {
    return this.querySelector(`.ncc-plan-picker__panel[data-mode="${this.dataset.mode}"] select`);
  }

  /**
   * @param {string} mode
   * @param {boolean} focus
   */
  #setMode(mode, focus) {
    if (!this.querySelector(`.ncc-plan-picker__panel[data-mode="${mode}"]`)) return;
    this.dataset.mode = mode;
    for (const tab of this.#tabs) {
      const on = tab.dataset.mode === mode;
      tab.setAttribute('aria-selected', on ? 'true' : 'false');
      tab.tabIndex = on ? 0 : -1;
      if (on && focus) tab.focus();
    }
    for (const panel of this.#panels) {
      const on = panel.dataset.mode === mode;
      panel.hidden = !on;
      for (const field of panel.querySelectorAll('select, textarea, input')) {
        /** @type {HTMLSelectElement | HTMLTextAreaElement | HTMLInputElement} */ (field).disabled = !on;
      }
    }
    this.#apply();
  }

  /** @param {Event} event */
  #onClick = (event) => {
    const tab = event.target instanceof Element ? event.target.closest('[role="tab"]') : null;
    if (tab instanceof HTMLElement && this.contains(tab) && tab.dataset.mode) this.#setMode(tab.dataset.mode, false);
  };

  /** @param {KeyboardEvent} event */
  #onKeydown = (event) => {
    const tab = event.target instanceof Element ? event.target.closest('[role="tab"]') : null;
    if (!(tab instanceof HTMLElement)) return;
    const tabs = this.#tabs;
    const index = tabs.indexOf(tab);
    let next = null;
    if (event.key === 'ArrowRight') next = tabs[(index + 1) % tabs.length];
    if (event.key === 'ArrowLeft') next = tabs[(index - 1 + tabs.length) % tabs.length];
    if (next instanceof HTMLElement && next.dataset.mode) {
      event.preventDefault();
      this.#setMode(next.dataset.mode, true);
    }
  };

  /** @param {Event} event */
  #onChange = (event) => {
    if (event.target instanceof HTMLSelectElement) this.#apply();
  };

  /** @param {Event} event */
  #onInput = (event) => {
    if (event.target instanceof HTMLTextAreaElement && event.target.classList.contains('ncc-plan-picker__message')) {
      this.#syncMessageName(event.target);
    }
  };

  /**
   * A variant change: Horizon replaces the price block's markup from the server's HTML, which also carries this
   * block rendered for the new variant - the options' prices are refreshed from it and the plan price written
   * over the new markup once it is in place (the price block replaces its container in the same promise).
   * @param {CustomEvent & { promise?: Promise<{ detail?: { html?: Document | Element } }> }} event
   */
  #onProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;
    event.promise
      ?.then(({ detail }) => {
        const fresh = detail?.html?.querySelector('ncc-plan-picker');
        if (fresh) {
          for (const option of this.querySelectorAll('option')) {
            const source = fresh.querySelector(`option[value="${option.value}"]`);
            if (!source) continue;
            for (const key of ['price', 'note', 'description']) option.dataset[key] = source.dataset[key] || '';
          }
        }
        window.setTimeout(() => this.#apply(), 0);
      })
      .catch(() => {});
  };

  /** @param {HTMLTextAreaElement} textarea */
  #syncMessageName(textarea) {
    if (textarea.value.trim()) textarea.setAttribute('name', textarea.dataset.name || 'properties[Gift message]');
    else textarea.removeAttribute('name');
  }

  #apply() {
    const select = this.#activeSelect;
    const option = select?.selectedOptions[0];
    const panel = select?.closest('.ncc-plan-picker__panel');
    if (!select || !option || !panel) return;
    const note = panel.querySelector('[data-ref="note"]');
    const nextNote = option.dataset.note || '';
    if (note && note.textContent !== nextNote) note.textContent = nextNote;
    const description = panel.querySelector('[data-ref="description"]');
    const nextDescription = option.dataset.description || '';
    if (description && description.textContent !== nextDescription) description.textContent = nextDescription;
    const message = panel.querySelector('.ncc-plan-picker__message');
    if (message instanceof HTMLTextAreaElement) this.#syncMessageName(message);
    this.#writePagePrice(option.dataset.price);
    this.#writeCtaLabel();
  }

  /**
   * The price block (`product-price`) and the sticky bar render the variant's price; a plan's price is the
   * allocation's (a prepaid gift = all its boxes), so it is written over both. Read fresh each time: Horizon
   * replaces the price block's markup on a variant change.
   * @param {string | undefined} price
   */
  #writePagePrice(price) {
    if (!price) return;
    const section = this.#section || document;
    for (const target of section.querySelectorAll('product-price .price, .sticky-add-to-cart__price .price')) {
      if (target.querySelector('.price')) continue; // a wrapper around the real price span
      if (target.textContent !== price) target.textContent = price;
    }
  }

  /**
   * The section's Add to Bag button reads the gift label (block setting) while a gift plan is chosen and its own
   * label otherwise. Its label is a bare text node between the icon and the "added" burst, so only that node is
   * touched; a disabled button (sold out, unavailable) keeps its wording.
   */
  #writeCtaLabel() {
    const gift = this.dataset.ctaGift;
    if (!gift) return;
    const button = (this.#section || document).querySelector('product-form-component button[type="submit"][name="add"]');
    if (!(button instanceof HTMLButtonElement) || button.disabled) return;
    const node = [...button.childNodes].find((child) => child.nodeType === Node.TEXT_NODE && child.textContent?.trim());
    if (!node) return;
    if (this.#ctaSubscribe === null) this.#ctaSubscribe = node.textContent || '';
    const next = this.dataset.mode === 'gift' ? gift : this.#ctaSubscribe;
    if (node.textContent !== next) node.textContent = next;
  }
}

if (!customElements.get('ncc-plan-picker')) customElements.define('ncc-plan-picker', PlanPicker);
